import type React from "react"
import { useMemo, useState } from "react"
import type {
  ColDef,
  GridApi,
  ICellRendererParams,
  IRowNode,
  PostSortRowsParams,
} from "ag-grid-community"
import { first } from "lodash"
import {
  createNumberColumn,
  createStringColumn,
  createUniqueStringColumn,
} from "~/utils/columnTemplates"
import { ProductTableColumn } from "~/constants/productTableColumns"
import { formatNumber, uniqueValueFormatter } from "~/utils/formatting"
import { formatSkuId, formatVariantId } from "~/utils/helpers"
import { uniq } from "~/utils/tsUtils"
import { sortSizesComparator } from "~/utils/sizeSplit"
import {
  WarehouseBreakdownTooltip,
  type BucketBreakdown,
} from "~/components/reusable/table/tooltips/WarehouseBreakdownTooltip"
import { DialogContentV2, DialogRoot } from "~/components/reusable/Dialog"
import { IncomingOrderCellDialogBody } from "~/components/purchaseOrder/incomingOrders/IncomingOrderCellRenderer"
import type { IncomingOrder } from "~/utils/incomingOrdersUtils"
import { type DayRange, spanDayRanges } from "./buyWindows"
import type { DynamicColumnId } from "~/components/planning/ProductTable/utils/columnUtils"

export type WarehouseSplit = Map<string, number>

export {
  createGroupingColumns,
  createVariantGroupingColumn,
  isVariantGroupLevel,
} from "./buyGroupingColumns"

export function createSkuColumn<T>(opts: { colId: string }): ColDef<T> {
  return createUniqueStringColumn<T>({
    colId: opts.colId as DynamicColumnId,
    field: "skuId" as never,
    headerName: "SKU",
    sortingField: "size",
    sortingType: "size",
    cellRenderer: (params: ICellRendererParams<T>) => {
      if (params.node?.group) {
        const count = params.node.allLeafChildren?.length ?? 0
        return count > 0 ? `${count} SKUs` : null
      }
      const data = params.data as { skuId?: string } | undefined
      const skuId = data?.skuId ?? first(params.value as string[])
      if (!skuId) return null
      return formatSkuId(skuId)
    },
    valueFormatter: (params) => uniqueValueFormatter(params, "SKUs"),
    tooltipTitle: "SKUs",
    uniqueSuffix: "SKUs",
    isIdentifier: true,
  })
}

export function createVariantIdColumn<T>(opts: { colId: string }): ColDef<T> {
  return createUniqueStringColumn<T>({
    colId: opts.colId as DynamicColumnId,
    field: "variantId" as never,
    headerName: "Variant ID",
    valueFormatter: (params) => uniqueValueFormatter(params, "Variant IDs"),
    cellRenderer: (params: ICellRendererParams<T>) => {
      const uniqueValues = uniq(
        params.node.allLeafChildren?.map(
          (node) => (node.data as { variantId?: string } | undefined)?.variantId,
        ) ?? [],
      )
      if (uniqueValues.length > 1) {
        return `${uniqueValues.length} Variant IDs`
      }
      const data = params.data as { variantId?: string } | undefined
      const variantId = data?.variantId ?? first(params.value as string[])
      if (!variantId) return null
      return formatVariantId(variantId)
    },
    tooltipTitle: "Variant IDs",
    uniqueSuffix: "Variant IDs",
  })
}

export function createProductColumn<T>(opts: { colId: string }): ColDef<T> {
  return createUniqueStringColumn<T>({
    colId: opts.colId as DynamicColumnId,
    field: "productName" as never,
    headerName: "Product",
    valueFormatter: (params) => uniqueValueFormatter(params, "Products"),
    tooltipTitle: "Products",
    uniqueSuffix: "Products",
  })
}

export function createSizeColumn<T>(opts: { colId: string }): ColDef<T> {
  return createStringColumn<T>({
    colId: opts.colId as DynamicColumnId,
    field: "size" as never,
    headerName: "Size",
    sortingField: "size",
    sortingType: "size",
  })
}

const SIZE_DIMENSION_COLUMN_IDS: ReadonlySet<string> = new Set([
  ProductTableColumn.SizeDimension1,
  ProductTableColumn.SizeDimension2,
])

function isSortedBySizeDimension(api: GridApi): boolean {
  return api.getColumnState().some((col) => col.sort && SIZE_DIMENSION_COLUMN_IDS.has(col.colId))
}

/** `postSortRows` for SKU tables: SKU rows stay in size order, unless sorted by a size dimension. */
export function keepSkusInSizeOrder(params: PostSortRowsParams): void {
  if (isSortedBySizeDimension(params.api)) return
  params.nodes.sort((a, b) => {
    if (a.group || b.group) return 0
    return sortSizesComparator(a.data?.size ?? "", b.data?.size ?? "")
  })
}

export function createSupplierSkuColumn<T>(opts: { colId: string; hide?: boolean }): ColDef<T> {
  return createStringColumn<T>({
    colId: opts.colId as DynamicColumnId,
    field: "supplierSku" as never,
    headerName: "Supplier SKU",
    hide: opts.hide ?? false,
  })
}

// Drill-down config: opt-in extension that turns the per-warehouse breakdown rows into
// clickable triggers opening an IncomingOrderCellDialogBody scoped to that warehouse's orders.
// Only set on columns that aggregate incoming-order quantities.
export type WarehouseAwareDrillDown<T> = {
  bucketLabel: string
  getVariantId: (row: T) => string | undefined
  getOrders: (row: T) => IncomingOrder[]
  // Maps a primary warehouse to the set of warehouses whose orders feed it (primary +
  // any added-demand warehouses folded into it).
  getFoldedWarehouseIds: (primaryWarehouseId: string) => string[]
  // Bucket window for this row. Either bound may be undefined for one-sided buckets
  // (e.g. "before ETA" has only end, "post period" has only start).
  getBucketRange: (row: T) => DayRange
}

// Precondition: callers must pass activeWarehouseIds.length > 0 OR a non-null selectedWarehouseId.
export function createWarehouseAwareNumberColumn<T>(opts: {
  colId: string
  headerName: string
  headerSubtitle?: string
  headerTooltip?: string
  label: string
  // 2nd arg carries AG-Grid's `context` so closures can read live data without
  // capturing it in a render-time closure (which would break `react-hooks/refs`).
  // Optional for backwards compatibility with callers that don't need it.
  getPerWarehouse: (row: T, ctx: { context?: unknown }) => Map<string, number>
  selectedWarehouseId: string | null
  activeWarehouseIds: string[]
  warehouseNames: Map<string, string>
  hide?: boolean
  unit?: string
  decimals?: number
  formatValue?: (n: number) => string
  caption?: string | ((row: T) => string | undefined)
  /**
   * Caption for group/footer rows, which have no leaf `data` of their own. Receives the node's leaf
   * rows so the caller can aggregate. Without it, grouped rows show the breakdown but no caption.
   */
  groupCaption?: (rows: T[]) => string | undefined
  buckets?: (rows: T[]) => BucketBreakdown | undefined
  initialWidth?: number
  aggregator?: "sum" | "avg"
  cellDisplay?: "total" | "minMax"
  drillDown?: WarehouseAwareDrillDown<T>
  filter?: boolean
  hideHeader?: boolean
  /**
   * Drop the summed "Total" line. For a stock POSITION the sum is meaningless and misleading — a
   * surplus in one warehouse cannot cover a shortfall in another, so netting them invites exactly
   * the arithmetic a planner must not do. Quantities owed or ordered are genuinely additive; keep it.
   */
  hideTotal?: boolean
}): ColDef<T> {
  const isSingleWarehouse = !!opts.selectedWarehouseId || opts.activeWarehouseIds.length <= 1
  const fmt = (n: number): string =>
    opts.formatValue
      ? opts.formatValue(n)
      : opts.decimals != null
        ? formatNumber(n, opts.decimals)
        : formatNumber(n)

  const reduce = (perWh: Map<string, number>): number => {
    if (opts.selectedWarehouseId) return perWh.get(opts.selectedWarehouseId) ?? 0
    let total = 0
    let count = 0
    for (const whId of opts.activeWarehouseIds) {
      total += perWh.get(whId) ?? 0
      count += 1
    }
    if (opts.aggregator === "avg") return count > 0 ? total / count : 0
    return total
  }

  // Iterates `activeWarehouseIds` (not the full leaf map keys) so warehouses outside the active set are intentionally excluded from group-row totals — same contract as the cell renderer below.
  const sumLeavesForActiveWarehouses = (
    node: IRowNode<T>,
    ctx: { context?: unknown },
  ): Map<string, number> => {
    const out = new Map<string, number>()
    const counts = new Map<string, number>()
    const leaves = node.allLeafChildren ?? []
    for (const leaf of leaves) {
      if (!leaf.data) continue
      // Transient node during rowData swaps may not be a full row.
      const perWh = opts.getPerWarehouse(leaf.data, ctx) ?? new Map<string, number>()
      for (const whId of opts.activeWarehouseIds) {
        out.set(whId, (out.get(whId) ?? 0) + (perWh.get(whId) ?? 0))
        counts.set(whId, (counts.get(whId) ?? 0) + 1)
      }
    }
    if (opts.aggregator === "avg") {
      for (const whId of opts.activeWarehouseIds) {
        const c = counts.get(whId) ?? 0
        if (c > 0) out.set(whId, (out.get(whId) ?? 0) / c)
      }
    }
    return out
  }

  const resolvePerWh = (params: {
    data?: T
    node?: IRowNode<T> | null
    context?: unknown
  }): Map<string, number> => {
    // Group/footer rows aggregate their leaves — check this BEFORE params.data. A grouped row can
    // carry a data object that isn't a full leaf row (e.g. a variant-level row), and computing from
    // it yields an empty/0 value for context-derived columns like Risk buy. Mirrors the group-first
    // order the plain columns (e.g. Risk buy %) already use.
    if (params.node && (params.node.group || params.node.footer)) {
      return sumLeavesForActiveWarehouses(params.node, { context: params.context })
    }
    if (params.data) {
      // Transient node may not be a full row (see sumLeavesForActiveWarehouses).
      return opts.getPerWarehouse(params.data, { context: params.context }) ?? new Map()
    }
    if (params.node) return sumLeavesForActiveWarehouses(params.node, { context: params.context })
    return new Map()
  }

  return createNumberColumn<T>({
    colId: opts.colId as DynamicColumnId,
    headerName: opts.headerName,
    headerSubtitle: opts.headerSubtitle,
    headerTooltip: opts.headerTooltip,
    hide: opts.hide,
    hasTooltip: false, // tooltip is rendered inline via HoverCard
    initialWidth: opts.initialWidth,
    filter: opts.filter,
    // aggFunc defaults to "sum" via createNumberColumn — applied to valueGetter's already-reduced scalars, which is correct for nested groupings.
    valueGetter: (params) => reduce(resolvePerWh(params)),
    valueFormatter: (params) => {
      if (params.value == null) return "-"
      return fmt(Number(params.value))
    },
    cellRenderer: (params: ICellRendererParams<T>) => {
      // NOTE: resolvePerWh runs again here after valueGetter — acceptable at expected scale (~hundreds of SKUs × handful of warehouses), revisit if profiling shows cell-render cost.
      // Footer nodes (grand total row) have group=true and allLeafChildren, so they fall through
      // to the same per-warehouse hover card path as group rows.
      const perWh = resolvePerWh(params)
      const total = reduce(perWh)

      const entries = (
        opts.selectedWarehouseId ? [opts.selectedWarehouseId] : opts.activeWarehouseIds
      ).map((whId) => ({
        id: whId,
        name: opts.warehouseNames.get(whId) ?? whId,
        value: perWh.get(whId) ?? 0,
      }))

      const isGroupNode = !!params.node && (params.node.group || params.node.footer)
      let leafRows: T[] = []
      if (isGroupNode) {
        leafRows = (params.node?.allLeafChildren ?? [])
          .map((leaf) => leaf.data)
          .filter((data): data is T => !!data)
      } else if (params.data) {
        leafRows = [params.data]
      }
      const resolvedCaption = isGroupNode
        ? opts.groupCaption?.(leafRows)
        : typeof opts.caption === "function"
          ? params.data
            ? opts.caption(params.data)
            : undefined
          : opts.caption
      const resolvedBuckets = leafRows.length > 0 ? opts.buckets?.(leafRows) : undefined

      let triggerValue: React.ReactNode
      if (opts.cellDisplay === "minMax") {
        if (opts.selectedWarehouseId) {
          triggerValue = fmt(perWh.get(opts.selectedWarehouseId) ?? 0)
        } else {
          let minVal = Infinity
          let maxVal = -Infinity
          for (const whId of opts.activeWarehouseIds) {
            const v = perWh.get(whId) ?? 0
            if (v < minVal) minVal = v
            if (v > maxVal) maxVal = v
          }
          if (!isFinite(minVal)) {
            triggerValue = fmt(0)
          } else if (minVal === maxVal) {
            triggerValue = fmt(minVal)
          } else {
            triggerValue = `${fmt(minVal)} - ${fmt(maxVal)}`
          }
        }
      } else {
        triggerValue = fmt(total)
      }

      if (opts.drillDown) {
        const drillDown = opts.drillDown
        const { variantIds, orders, bucketRange } = collectDrillDownData(params, drillDown)
        return (
          <WarehouseAwareDrillDownCell
            label={opts.label}
            total={total}
            perWarehouse={entries}
            formatValue={fmt}
            unit={opts.unit}
            isSingleWarehouse={isSingleWarehouse}
            caption={resolvedCaption}
            buckets={resolvedBuckets}
            trigger={triggerValue}
            bucketLabel={drillDown.bucketLabel}
            variantIds={variantIds}
            orders={orders}
            bucketRange={bucketRange}
            getFoldedWarehouseIds={drillDown.getFoldedWarehouseIds}
            hideHeader={opts.hideHeader}
          />
        )
      }
      // A single warehouse needs no breakdown — but a caption carries the explanation of how the
      // number was reached, so keep the hovercard whenever one is present.
      if (isSingleWarehouse && !resolvedCaption && !resolvedBuckets) {
        return <span className="flex h-full w-full items-center">{triggerValue}</span>
      }

      return (
        <WarehouseBreakdownTooltip
          label={opts.label}
          total={total}
          perWarehouse={entries}
          formatValue={fmt}
          unit={opts.unit}
          isSingleWarehouse={isSingleWarehouse}
          caption={resolvedCaption}
          buckets={resolvedBuckets}
          trigger={triggerValue}
          hideHeader={opts.hideHeader}
          hideTotal={opts.hideTotal}
        />
      )
    },
  })
}

// Pulls drill-down inputs out of cell params: leaf rows return their own values; group rows
// union variantIds, orders, and bucket window across all leaves so the dialog covers everything
// that contributed to the aggregated total.
function collectDrillDownData<T>(
  params: { data?: T; node?: IRowNode<T> | null },
  drillDown: WarehouseAwareDrillDown<T>,
): { variantIds: string[]; orders: IncomingOrder[]; bucketRange: DayRange } {
  if (params.data) {
    const vid = drillDown.getVariantId(params.data)
    return {
      variantIds: vid ? [vid] : [],
      orders: drillDown.getOrders(params.data),
      bucketRange: drillDown.getBucketRange(params.data),
    }
  }
  if (params.node) {
    const ids = new Set<string>()
    const orders: IncomingOrder[] = []
    const ranges: DayRange[] = []
    for (const leaf of params.node.allLeafChildren ?? []) {
      if (!leaf.data) continue
      const vid = drillDown.getVariantId(leaf.data)
      if (vid) ids.add(vid)
      for (const o of drillDown.getOrders(leaf.data)) orders.push(o)
      ranges.push(drillDown.getBucketRange(leaf.data))
    }
    return {
      variantIds: Array.from(ids),
      orders,
      bucketRange: spanDayRanges(ranges),
    }
  }
  return { variantIds: [], orders: [], bucketRange: {} }
}

// Wraps WarehouseBreakdownTooltip in a Dialog: clicking a per-warehouse row scopes the dialog
// to that warehouse (folded with any added-demand warehouses) and opens the incoming-orders body.
function WarehouseAwareDrillDownCell({
  label,
  total,
  perWarehouse,
  formatValue,
  unit,
  isSingleWarehouse,
  caption,
  buckets,
  trigger,
  bucketLabel,
  variantIds,
  orders,
  bucketRange,
  getFoldedWarehouseIds,
  hideHeader,
}: {
  label: string
  total: number
  perWarehouse: { id: string; name: string; value: number }[]
  formatValue: (n: number) => string
  unit?: string
  isSingleWarehouse: boolean
  caption?: string
  buckets?: BucketBreakdown
  trigger: React.ReactNode
  bucketLabel: string
  variantIds: string[]
  orders: IncomingOrder[]
  bucketRange: DayRange
  getFoldedWarehouseIds: (primaryWarehouseId: string) => string[]
  hideHeader?: boolean
}) {
  const [open, setOpen] = useState(false)
  const [showAllToggle, setShowAllToggle] = useState(false)
  const [scopedWarehouseIds, setScopedWarehouseIds] = useState<string[] | undefined>()

  const scopedOrders = useMemo(() => {
    if (!scopedWarehouseIds || scopedWarehouseIds.length === 0) return orders
    const allowed = new Set(scopedWarehouseIds)
    return orders.filter((o) => allowed.has(o.warehouseId))
  }, [orders, scopedWarehouseIds])

  const canOpen = variantIds.length > 0

  const onOpenChange = (next: boolean) => {
    setOpen(next)
    if (!next) {
      setShowAllToggle(false)
      setScopedWarehouseIds(undefined)
    }
  }

  const only = perWarehouse.length === 1 ? perWarehouse[0] : undefined

  const tooltip = (
    <WarehouseBreakdownTooltip
      label={label}
      total={total}
      perWarehouse={perWarehouse}
      formatValue={formatValue}
      unit={unit}
      isSingleWarehouse={isSingleWarehouse}
      caption={caption}
      buckets={buckets}
      trigger={trigger}
      hideHeader={hideHeader}
      onRowClick={
        canOpen && !only
          ? (id) => {
              setScopedWarehouseIds(getFoldedWarehouseIds(id))
              setOpen(true)
            }
          : undefined
      }
    />
  )

  if (!canOpen) return tooltip

  return (
    <DialogRoot open={open} onOpenChange={onOpenChange}>
      {only ? (
        <button
          type="button"
          className="flex h-full w-full items-center text-left cursor-pointer"
          onClick={() => {
            setScopedWarehouseIds(getFoldedWarehouseIds(only.id))
            setOpen(true)
          }}
        >
          {trigger}
        </button>
      ) : (
        tooltip
      )}
      <DialogContentV2 className="max-w-[min(95vw,1280px)]">
        {open && (
          <IncomingOrderCellDialogBody
            variantIds={variantIds}
            startDate={bucketRange.start}
            endDate={bucketRange.end}
            showAll={showAllToggle || (!bucketRange.start && !bucketRange.end)}
            showAllToggle={showAllToggle}
            setShowAllToggle={setShowAllToggle}
            bucketLabel={bucketLabel}
            orders={scopedOrders}
            open={open}
            warehouseIds={scopedWarehouseIds}
          />
        )}
      </DialogContentV2>
    </DialogRoot>
  )
}
